import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { Tool } from '../tools/Tool';
import type { EnhancedToolResult, ToolDefinition, ToolExecutionContext } from '../types';
import { resolveSceneById, sceneDisplayId } from '../tools/scene-addressing';
import { resolveDesignTarget } from './designTargeting';
import { isHardCut, markedCarryIds, seamsAsPlayed } from './seamCheck';

import { intToHex } from 'js/src/libs/color-utils';

import { contrastRatio, parseColor } from './lint/contrast';
import {
    parseDesignTokens,
    plotBoxOf,
    REQUIRED_DESIGN_TOKENS,
    tokenPx,
    TYPE_TOKENS,
} from './lint/designSystemTokens';

import type { DesignMotif, SceneHandoff } from 'js/src/data/html-design';
import { getHtmlDesignSystem, setHtmlDesignSystem } from 'js/src/data/html-design';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';

/** Matches the safe margin the bounds lint enforces, so a system can't licence a cramped edge. */
const MIN_MARGIN_PX = 80;
/** The readable-text norm the size lint enforces: the smallest type in the scale must clear it. */
const MIN_TYPE_PCT = 4.5;
/** The baseline is the plot box's own foot; a px of rounding between them is not a disagreement. */
const BASELINE_TOL_PX = 1;

/** The smallest type this canvas allows, which is what `rejections` measures --ds-type-label by. */
function typeFloorPx(canvasHeight: number): number {
    return Math.ceil((MIN_TYPE_PCT / 100) * canvasHeight);
}

export const writeDesignSystemSchema = z.object({
    look: z
        .string()
        .optional()
        .describe(
            'The look in one or two sentences, e.g. "flat and geometric, one accent colour, ' +
                'generous margins, no gradients". What the tokens below are expressing.',
        ),
    css: z
        .string()
        .optional()
        .describe(
            'CSS (no <style> tag) defining every --ds-* token on :root: ' +
                `${REQUIRED_DESIGN_TOKENS.join(', ')}. The three type sizes are px, largest ` +
                'first; --ds-margin and the --ds-plot-* box are px; --ds-baseline-y is ' +
                '--ds-plot-y + --ds-plot-h; --ds-accent, --ds-ink and --ds-bg name the ' +
                'var(--l5-color-N) slots they use. May also define .ds-hero, .ds-headline, ' +
                '.ds-label and .ds-plot utility classes that apply them, and the .ds-motif-* ' +
                'classes. ' +
                `Two floors are rejected rather than warned about: --ds-type-label is the video's ` +
                `smallest type, read across a room rather than at a desk, so at least ` +
                `${MIN_TYPE_PCT}% of the canvas height; and --ds-margin is at least ` +
                `${MIN_MARGIN_PX}px.`,
        ),
    motifs: z
        .array(
            z.object({
                class: z
                    .string()
                    .describe(
                        'The `ds-motif-<name>` class your css defines for it, without the dot.',
                    ),
                what: z.string().describe('What it is and where it recurs.'),
            }),
        )
        .optional()
        .describe(
            'Elements that recur across graphics — a section marker, a running total, the rail a ' +
                'journey travels along. Each is drawn by one `.ds-motif-<name>` class in css (its ' +
                'stroke, colour, size, and position when it always sits in one place), so every ' +
                'graphic draws the same thing rather than re-inventing it. Omit when nothing recurs.',
        ),
    handoffs: z
        .array(
            z.object({
                id: z
                    .string()
                    .describe(
                        'Short kebab-case name, e.g. "revenue-bar". An element carried through ' +
                            'several seams keeps one id through all of them.',
                    ),
                until_scene_id: z
                    .string()
                    .optional()
                    .describe(
                        'The last graphic to receive the element, when it is carried on through ' +
                            'every seam from from_scene_id up to that graphic. Omit for one seam.',
                    ),
                from_scene_id: z
                    .string()
                    .describe(
                        'The graphic that hands the element on; the next graphic receives it.',
                    ),
                element: z.string().describe('What is carried, e.g. "the 2019 bar at 6%".'),
            }),
        )
        .optional()
        .describe(
            'Seams where the next graphic continues from this one instead of cutting to a fresh ' +
                'frame: an element left on screen at the end of from_scene_id that the following ' +
                'graphic opens on, in the same pose, and then develops — the bar that grows into ' +
                'the next chart, the figure that shrinks into a header, the parcel that keeps ' +
                'travelling, the timeline that each graphic extends. One earns its place where the ' +
                'next sentence continues the same figure or object. The graphic handing it on ' +
                'marks the element data-l5-carry="<id>", and the next one opens on an exact copy of ' +
                'it, so the seam matches by construction. A later call adds these to the handoffs ' +
                'already declared, and one declared again for the same seam replaces it; take one ' +
                'out with drop_handoffs.',
        ),
    drop_handoffs: z
        .array(
            z.object({
                id: z.string(),
                from_scene_id: z.string().describe('The graphic the handoff leaves.'),
            }),
        )
        .optional()
        .describe(
            'Declared handoffs to take out, by id and the scene they leave — for a seam that should ' +
                'become a plain cut. Leaving a handoff out of handoffs keeps it.',
        ),
});
export type WriteDesignSystemArgs = z.infer<typeof writeDesignSystemSchema>;

/**
 * Reasons the submitted CSS can't serve as the video's system. Every check names the token at fault:
 * the agent re-submits the whole block, so a finding it can't locate costs a round trip.
 */
function rejections(css: string, canvasHeight: number): string[] {
    const tokens = parseDesignTokens(css);
    const missing = REQUIRED_DESIGN_TOKENS.filter((name) => !tokens.has(name));
    if (missing.length > 0) {
        return [
            `Missing ${missing.length} required token(s): ${missing.join(', ')}. Every graphic in ` +
                `this video is sized and drawn from these, so one left undeclared is one each ` +
                `composition invents for itself.`,
        ];
    }

    const problems: string[] = [];
    const [hero, headline, label] = TYPE_TOKENS.map((name) => tokenPx(tokens, name));
    if (hero === null || headline === null || label === null) {
        problems.push(
            `${TYPE_TOKENS.join(', ')} must each be a px size — they are the video's type scale.`,
        );
    } else {
        if (headline > hero) {
            problems.push(
                `--ds-type-headline (${headline}px) is larger than --ds-type-hero (${hero}px), ` +
                    `which inverts the scale. Order them hero ≥ headline ≥ label.`,
            );
        }
        if (label > headline) {
            problems.push(
                `--ds-type-label (${label}px) is larger than --ds-type-headline (${headline}px), ` +
                    `which inverts the scale. Order them hero ≥ headline ≥ label.`,
            );
        }
        const floorPx = typeFloorPx(canvasHeight);
        if (label < floorPx) {
            problems.push(
                `--ds-type-label (${label}px) is below the ${MIN_TYPE_PCT}% readable norm for ` +
                    `motion-graphic text, which is ${floorPx}px on this ${canvasHeight}px-tall ` +
                    `canvas. Raise it — the smallest size in the scale is still on-screen copy.`,
            );
        }
    }

    const margin = tokenPx(tokens, '--ds-margin');
    if (margin === null || margin < MIN_MARGIN_PX) {
        problems.push(
            `--ds-margin (${tokens.get('--ds-margin')}) must be at least ${MIN_MARGIN_PX}px — ` +
                `content inside that gutter reads as cramped at the canvas edge, and the bounds ` +
                `lint reports it.`,
        );
    }

    const plot = plotBoxOf(tokens);
    if (!plot) {
        problems.push(
            `--ds-plot-x, --ds-plot-y, --ds-plot-w, --ds-plot-h and --ds-baseline-y must each be ` +
                `a px value — they are the one box every data graphic is drawn in.`,
        );
    } else if (Math.abs(plot.baselineY - (plot.y + plot.height)) > BASELINE_TOL_PX) {
        problems.push(
            `--ds-baseline-y (${plot.baselineY}px) is not the foot of the plot box ` +
                `(--ds-plot-y ${plot.y}px + --ds-plot-h ${plot.height}px = ` +
                `${plot.y + plot.height}px). Series are anchored to the baseline and the box is ` +
                `what they are measured in, so the two disagreeing puts every chart off its axis.`,
        );
    }

    return problems;
}

const MOTIF_CLASS = /^ds-motif-[a-z0-9-]+$/;
const HANDOFF_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function motifRejections(motifs: DesignMotif[], css: string): string[] {
    return motifs.flatMap(({ className }) => {
        if (!MOTIF_CLASS.test(className)) {
            return [`Motif class "${className}" must be named ds-motif-<name>, in kebab-case.`];
        }
        // The name is already known to be [a-z0-9-], so it needs no escaping to sit in a pattern.
        if (!new RegExp(`\\.${className}(?![\\w-])`).test(css)) {
            return [
                `Motif "${className}" has no .${className} rule in css. The class is what makes ` +
                    `every graphic draw the motif the same way, so define it there.`,
            ];
        }
        return [];
    });
}

interface MotionGraphicsSeam {
    sceneId: string;
    nextSceneId: string | null;
    straightCut: boolean;
}

/** The handoffs resolved onto real scene pairs, one per seam, or the reasons they can't be. */
function resolveHandoffs(
    handoffs: WriteDesignSystemArgs['handoffs'],
    resolveScene: (sceneId: string) => MotionGraphicsSeam | string,
    sceneIndex: (sceneId: string) => number | string,
): { handoffs: SceneHandoff[]; problems: string[] } {
    const resolved: SceneHandoff[] = [];
    const problems: string[] = [];
    // One element carried through several seams keeps its id through all of them, so an id may
    // recur — but only once per seam.
    const seen = new Set<string>();
    for (const handoff of handoffs ?? []) {
        const { id, from_scene_id: fromSceneId, until_scene_id: untilSceneId, element } = handoff;
        if (!HANDOFF_ID.test(id)) {
            problems.push(`Handoff id "${id}" must be kebab-case, e.g. "revenue-bar".`);
            continue;
        }
        let untilIndex: number | null = null;
        if (untilSceneId !== undefined) {
            const from = sceneIndex(fromSceneId);
            const until = sceneIndex(untilSceneId);
            if (typeof from === 'string' || typeof until === 'string') {
                problems.push(`Handoff "${id}": ${typeof from === 'string' ? from : until}`);
                continue;
            }
            if (until <= from) {
                problems.push(
                    `Handoff "${id}": scene ${untilSceneId} does not play after scene ` +
                        `${fromSceneId}, so the element can't be carried on until it.`,
                );
                continue;
            }
            untilIndex = until;
        }
        let giver = fromSceneId;
        for (;;) {
            const seam = resolveSeam(id, giver, resolveScene, seen);
            if (typeof seam === 'string') {
                problems.push(seam);
                break;
            }
            resolved.push({ id, fromSceneId: seam.sceneId, toSceneId: seam.nextSceneId, element });
            if (untilIndex === null || sceneIndex(seam.nextSceneId) === untilIndex) {
                break;
            }
            giver = seam.nextSceneId;
        }
    }
    return { handoffs: resolved, problems };
}

/** The one seam out of `fromSceneId` an element is handed across, or why it can't be. */
function resolveSeam(
    id: string,
    fromSceneId: string,
    resolveScene: (sceneId: string) => MotionGraphicsSeam | string,
    seen: Set<string>,
): { sceneId: string; nextSceneId: string } | string {
    const scene = resolveScene(fromSceneId);
    if (typeof scene === 'string') {
        return `Handoff "${id}": ${scene}`;
    }
    const seam = `${id}@${scene.sceneId}`;
    if (seen.has(seam)) {
        return `Handoff "${id}" is declared twice out of scene ${fromSceneId}.`;
    }
    seen.add(seam);
    if (!scene.nextSceneId) {
        return (
            `Handoff "${id}": scene ${fromSceneId} is not followed by another motion graphics ` +
            `scene, so there is nothing to hand the element to.`
        );
    }
    if (!scene.straightCut) {
        return (
            `Handoff "${id}": a transition or match cut plays over the seam after scene ` +
            `${fromSceneId}, so no pose can line up across it. Make it a straight cut, or ` +
            `leave this seam without a handoff.`
        );
    }
    return { sceneId: scene.sceneId, nextSceneId: scene.nextSceneId };
}

/**
 * Authors the one design system the whole video inherits. Video-level, so unlike the other design
 * tools it takes no scene_id: it is called once, before the first plan, and what it stores is the
 * contract every composition written afterwards is measured against by the design-system lint.
 */
/** WCAG AA for large text, which is what a design system's type sizes always are. */
const LARGE_TEXT_CONTRAST = 3;

/**
 * The colour a token resolves to, as hex. A token names either a literal or one of the video's
 * palette slots (`var(--l5-color-2)`), and only the slots need the template to resolve.
 */
function resolveColour(raw: string | undefined, palette: number[]): string | null {
    if (!raw) return null;
    const slot = raw.match(/--l5-color-(\d+)/);
    if (slot) {
        const colour = palette[Number(slot[1])];
        return colour === undefined ? null : intToHex(colour);
    }
    const literal = raw.match(/#[0-9a-f]{3,8}|rgba?\([^)]+\)/i);
    return literal ? literal[0] : null;
}

/**
 * A note about the accent, when it is too close to the background to be read as words.
 *
 * Not a rejection: an accent is chosen to read as a *shape*, and the palette may hold nothing that
 * clears the text threshold — refusing the system would be a block with no fix, which is the worst
 * thing this tool can do. But an accent at 2.4:1 is where the contrast lint's findings come from,
 * a dozen a build, so the author is better told now than told twelve times later.
 */
function accentReadabilityNote(css: string, palette: number[]): string | null {
    const tokens = parseDesignTokens(css);
    const accent = parseColor(resolveColour(tokens.get('--ds-accent'), palette) ?? '');
    const background = parseColor(resolveColour(tokens.get('--ds-bg'), palette) ?? '');
    if (!accent || !background) return null;
    const ratio = contrastRatio(accent, background);
    if (ratio >= LARGE_TEXT_CONTRAST) return null;
    return (
        `Note: --ds-accent reads at ${ratio.toFixed(2)}:1 against --ds-bg, under the ` +
        `${LARGE_TEXT_CONTRAST}:1 a viewer needs for text. It works for shapes; set type in ` +
        `var(--ds-ink) and keep the accent for what is drawn, or the contrast lint will report ` +
        `every line you colour with it.`
    );
}

export class WriteDesignSystemTool extends Tool<typeof writeDesignSystemSchema> {
    protected override readonly schema = writeDesignSystemSchema;
    protected override readonly toolName = TOOL_NAMES.writeDesignSystem;
    public override readonly subagentAccess = SubagentAccess.BLOCKED;
    protected override readonly toolDescription =
        "Author the video's design system: the type scale, spacing, stroke weight, chart box and " +
        'colour roles that every graphic in this video is built from. Call this before the ' +
        `first ${TOOL_NAMES.writePlan}; it hands back a \`:root\` block to open every composition ` +
        'with. A later call sends only what it changes: a look, css or motifs left out keeps the ' +
        'stored one. Returns an error listing what is missing or inconsistent if the tokens do not hold ' +
        'together; fix and call again.';

    /**
     * Names the type floor in pixels, for this video's own canvas. The floor is a percentage of
     * canvas height and the field wants px, so an author reading only the percentage has to convert
     * — and a video is authored at any of four reference resolutions, so a figure quoted for one of
     * them would be wrong on the other three. Left as the percentage alone when there is no
     * resolution to read (a tool built without a luminary, as the schema guardrail spec does).
     */
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        const canvasHeight = this.luminary?.referenceResolution?.height;
        if (!canvasHeight) {
            return parameters;
        }
        const css = parameters.properties?.css as { description?: string } | undefined;
        if (!css?.description) {
            return parameters;
        }
        return {
            ...parameters,
            properties: {
                ...parameters.properties,
                css: {
                    ...css,
                    description: css.description.replace(
                        `${MIN_TYPE_PCT}% of the canvas height`,
                        `${MIN_TYPE_PCT}% of this video's ${canvasHeight}px canvas height, which is ` +
                            `${typeFloorPx(canvasHeight)}px`,
                    ),
                },
            },
        };
    }

    public getToolDisplayName(): string {
        return 'Designing the system';
    }

    /**
     * A handoff declared once its graphics are built — the usual way a seam is changed from a cut —
     * is carried by nothing until the built side marks it, and that graphic is not the one the
     * agent is working on next, so it has to be told which.
     */
    private unmarkedCarriesNote(handoffs: SceneHandoff[]): string | null {
        const unmarkedIn = (sceneId: string, id: string): boolean => {
            const target = resolveDesignTarget(this.getState(), this.luminary, sceneId);
            const html = 'error' in target ? '' : target.asset.html;
            return Boolean(html) && !markedCarryIds(html).has(id);
        };
        const where = (handoff: SceneHandoff, sceneId: string): string =>
            `"${handoff.id}" in scene ${this.displayId(sceneId)}`;
        const giving = handoffs
            .filter((handoff) => unmarkedIn(handoff.fromSceneId, handoff.id))
            .map((handoff) => where(handoff, handoff.fromSceneId));
        const receiving = handoffs
            .filter((handoff) => unmarkedIn(handoff.toSceneId, handoff.id))
            .map((handoff) => where(handoff, handoff.toSceneId));
        const notes = [
            giving.length &&
                `Already built, and not yet handing on its carry: ${giving.join(', ')}. Mark the ` +
                    `element there data-l5-carry="<id>" with ${TOOL_NAMES.editHtmlAsset}, keeping ` +
                    `it on screen through its last frame.`,
            receiving.length &&
                `Already built, and not yet opening on its carry: ${receiving.join(', ')}. Once ` +
                    `the graphic before marks it, re-write that graphic with ` +
                    `<div data-l5-carry-in="<id>"></div> in place of its own version, so it opens ` +
                    `on a copy.`,
        ].filter((note): note is string => Boolean(note));
        return notes.length ? notes.join(' ') : null;
    }

    /**
     * Handoffs the stored system had that this one leaves out, where a graphic on either side is
     * already built. Dropping one is legitimate — a seam can become a cut — but it is also how a
     * failing carry gets made to go quiet, and once it is gone no check looks at that seam again.
     */
    private droppedCarriesNote(handoffs: SceneHandoff[]): string | null {
        const kept = new Set(handoffs.map(({ id, fromSceneId }) => `${id}@${fromSceneId}`));
        const built = (sceneId: string): boolean => {
            const target = resolveDesignTarget(this.getState(), this.luminary, sceneId);
            return !('error' in target) && Boolean(target.asset.html);
        };
        const dropped = (getHtmlDesignSystem(this.getState())?.handoffs ?? []).filter(
            (handoff) => !kept.has(`${handoff.id}@${handoff.fromSceneId}`),
        );
        if (dropped.length === 0) {
            return null;
        }
        const seams = dropped
            .map(
                ({ id, fromSceneId, toSceneId }) =>
                    `"${id}" from scene ${this.displayId(fromSceneId)} into ` +
                    `${this.displayId(toSceneId)}${built(fromSceneId) || built(toSceneId) ? ' (built)' : ''}`,
            )
            .join(', ');
        return (
            `This drops ${seams}, and nothing checks those seams now — declare one again if it was ` +
            `meant to stay, and if its carry was failing, fix the carry instead.`
        );
    }

    /**
     * The declared handoffs a call takes out. One that names nothing declared fails the call: left
     * quiet, the seam would stay enforced while the agent believes it a plain cut.
     */
    private handoffsToDrop(
        drops: WriteDesignSystemArgs['drop_handoffs'],
        stored: SceneHandoff[],
    ): { toDrop: SceneHandoff[]; problems: string[] } {
        const toDrop: SceneHandoff[] = [];
        const problems: string[] = [];
        for (const { id, from_scene_id: fromSceneId } of drops ?? []) {
            const scene = resolveSceneById(this.luminary, this.getState(), fromSceneId);
            if ('error' in scene) {
                problems.push(`drop_handoffs "${id}": ${scene.error}`);
                continue;
            }
            const matched = stored.filter(
                (handoff) => handoff.id === id && handoff.fromSceneId === scene.scene.id,
            );
            if (matched.length === 0) {
                problems.push(
                    `drop_handoffs "${id}": no handoff "${id}" is declared out of scene ` +
                        `${fromSceneId}, so there is nothing to drop there.`,
                );
            }
            toDrop.push(...matched);
        }
        return { toDrop, problems };
    }

    private displayId(sceneId: string): string {
        return sceneDisplayId(this.luminary, sceneId);
    }

    /** The scene a handoff leaves, and the motion graphics scene straight after it that receives it. */
    private motionGraphicsSeam(sceneId: string): MotionGraphicsSeam | string {
        const resolved = resolveDesignTarget(this.getState(), this.luminary, sceneId);
        if ('error' in resolved) {
            return resolved.error.result.replace(/^Error: /, '');
        }
        const scenes = this.luminary.scenes;
        const next = scenes[scenes.indexOf(resolved.scene) + 1];
        const nextIsGraphic =
            next !== undefined &&
            !('error' in resolveDesignTarget(this.getState(), this.luminary, next.id));
        return {
            sceneId: resolved.scene.id,
            nextSceneId: nextIsGraphic ? next.id : null,
            straightCut: nextIsGraphic && isHardCut(resolved.scene, next),
        };
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public async execute(
        args: WriteDesignSystemArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const storedSystem = getHtmlDesignSystem(this.getState());
        const look = args.look ?? storedSystem?.look;
        const css = args.css ?? storedSystem?.css;
        if (look === undefined || css === undefined) {
            return {
                result:
                    'The design system was not accepted: none is stored yet, so this first call ' +
                    'needs both look and css.',
                isSuccess: false,
            };
        }
        const cssChanged = css !== storedSystem?.css;
        const { handoffs, problems: handoffProblems } = resolveHandoffs(
            args.handoffs,
            (id) => this.motionGraphicsSeam(id),
            (id) => {
                const scene = resolveSceneById(this.luminary, this.getState(), id);
                return 'error' in scene ? scene.error : scene.sceneIndex;
            },
        );
        const stored = storedSystem?.handoffs ?? [];
        const { toDrop, problems: dropProblems } = this.handoffsToDrop(args.drop_handoffs, stored);
        const motifs: DesignMotif[] =
            args.motifs?.map(({ class: className, what }) => ({ className, what })) ??
            storedSystem?.motifs ??
            [];
        const problems = [
            // Stored css passed these when it was written, against the same canvas.
            ...(cssChanged ? rejections(css, this.luminary.referenceResolution.height) : []),
            ...motifRejections(motifs, css),
            ...handoffProblems,
            ...dropProblems,
        ];
        if (problems.length > 0) {
            return {
                result: `The design system was not accepted:\n${problems
                    .map((problem) => `- ${problem}`)
                    .join('\n')}`,
                isSuccess: false,
            };
        }

        const seamOf = ({ id, fromSceneId }: { id: string; fromSceneId: string }): string =>
            `${id}@${fromSceneId}`;
        const redeclared = new Set(handoffs.map(seamOf));
        // A seam that no longer exists is not kept on: nothing can check it, and it would only
        // be reported again on every graphic either side of where it was.
        const { moved } = seamsAsPlayed(this.luminary, { incoming: stored, outgoing: [] });
        const kept = stored.filter(
            (handoff) =>
                !redeclared.has(seamOf(handoff)) &&
                !toDrop.includes(handoff) &&
                !moved.includes(handoff),
        );
        const merged = [...kept, ...handoffs];
        // Read before the new system replaces it: what this call takes away is the one thing nothing
        // afterwards can see.
        const dropped = this.droppedCarriesNote(merged);
        this.dispatch(setHtmlDesignSystem({ look, css, motifs, handoffs: merged }));
        const palette = (this.luminary.template?.colors ?? []).map((slot) => slot.color);
        const notes = [
            cssChanged ? accentReadabilityNote(css, palette) : null,
            this.unmarkedCarriesNote(handoffs),
            dropped,
        ]
            .filter(Boolean)
            .map((note) => `\n\n${note}`)
            .join('');
        // Echoed again unchanged, the block reads as a new system and sends the agent back to
        // re-open every graphic already built with what it already has.
        const summary = cssChanged
            ? 'Design system stored. Open every composition you write for this video with it, ' +
              `before your own styles:\n\n<style>\n${css}\n</style>`
            : 'Design system stored. Its css is unchanged, so graphics already built keep it as ' +
              'they are.';
        return { result: summary + notes, isSuccess: true };
    }
}
